import {
  CampaignNoiseProcessingRequestDto,
  CampaignNoiseProcessingResponseDto,
} from '@shared/services/apiService/apiService';

import {
  CampaignNoiseCancellationDraft,
  mapCampaignNoiseCancellationToDraft,
  mapCampaignNoiseCancellationDraftToRequest,
  validateCampaignNoiseCancellationDraft,
} from './campaignNoiseCancellation.utils';
import { NoiseCancellationValidation } from './noiseProcessing.utils';

export type NoiseGuardDraft = {
  enabled: boolean;
  shadowMode: boolean;
  hangupEnabled: boolean;
  triggers: string;
};

export type NoiseGuardValidation = {
  triggers?: string;
};

export type CampaignNoiseProcessingDraft = {
  noiseGuard: NoiseGuardDraft;
  noiseCancellation: CampaignNoiseCancellationDraft;
};

export type CampaignNoiseProcessingValidation = {
  noiseGuard: NoiseGuardValidation;
  noiseCancellation: NoiseCancellationValidation;
};

const stringifyJson = (value?: object | null) =>
  value == null ? '' : JSON.stringify(value, null, 2);

const parseJsonObject = (value: string) => {
  if (!value.trim()) return { value: undefined, error: undefined };

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { value: undefined, error: 'noiseProcessing.validation.jsonObject' };
    }

    return { value: parsed as Record<string, unknown>, error: undefined };
  } catch {
    return { value: undefined, error: 'noiseProcessing.validation.invalidJson' };
  }
};

export const mapNoiseGuardToDraft = (
  value?: CampaignNoiseProcessingResponseDto['noiseGuard'],
): NoiseGuardDraft => ({
  enabled: Boolean(value?.enabled),
  shadowMode: Boolean(value?.shadowMode),
  hangupEnabled: Boolean(value?.hangupEnabled),
  triggers: stringifyJson(value?.triggers),
});

export const mapCampaignNoiseProcessingToDraft = (
  value?: CampaignNoiseProcessingResponseDto | null,
): CampaignNoiseProcessingDraft => ({
  noiseGuard: mapNoiseGuardToDraft(value?.noiseGuard),
  noiseCancellation: mapCampaignNoiseCancellationToDraft(value),
});

export const cloneCampaignNoiseProcessingDraft = (
  draft: CampaignNoiseProcessingDraft,
): CampaignNoiseProcessingDraft => ({
  noiseGuard: { ...draft.noiseGuard },
  noiseCancellation: { ...draft.noiseCancellation },
});

export const validateNoiseGuardDraft = (draft: NoiseGuardDraft): NoiseGuardValidation => {
  const parsed = parseJsonObject(draft.triggers);
  return parsed.error ? { triggers: parsed.error } : {};
};

export const validateCampaignNoiseProcessingDraft = (
  draft: CampaignNoiseProcessingDraft,
): CampaignNoiseProcessingValidation => ({
  noiseGuard: validateNoiseGuardDraft(draft.noiseGuard),
  noiseCancellation: validateCampaignNoiseCancellationDraft(draft.noiseCancellation),
});

export const mapNoiseGuardDraftToRequest = (
  draft: NoiseGuardDraft,
): NonNullable<CampaignNoiseProcessingRequestDto['noiseGuard']> => {
  const request: NonNullable<CampaignNoiseProcessingRequestDto['noiseGuard']> = {
    enabled: draft.enabled,
    shadowMode: draft.shadowMode,
    hangupEnabled: draft.hangupEnabled,
  };
  const parsed = parseJsonObject(draft.triggers);

  if (parsed.value) request.triggers = parsed.value;

  return request;
};

export const mapCampaignNoiseProcessingDraftToRequest = (
  draft: CampaignNoiseProcessingDraft,
): CampaignNoiseProcessingRequestDto => ({
  noiseGuard: mapNoiseGuardDraftToRequest(draft.noiseGuard),
  ...mapCampaignNoiseCancellationDraftToRequest(draft.noiseCancellation),
});
